/** Calendar cash forecast. Opening cash is after existing card reserves, at day end.
 * Future card purchases are reserved when spent; repayments of those purchases
 * must not be entered again. This is available household cash, not a bank balance.
 */
export interface CashBill {
  id: string;
  name: string;
  amountAed: number;
  firstDue: string;
  repeatMonths?: number;
  lastDue?: string;
}
export interface RunwayInput {
  asOf: string;
  cashAed: number;
  weeklyEverydayAed: number;
  weeklySavingAed?: number;
  bills: CashBill[];
  /** Last date for which all known commitments have been included. */
  coveredThrough: string;
}
export interface CashDay {
  date: string;
  remainingAed: number;
  everydayAed: number;
  bills: Array<{ id: string; name: string; amountAed: number }>;
}
export interface RunwayResult {
  /** First day the available cash reaches zero or cannot cover that day's costs. */
  needsSavingsOn: string | null;
  lastFullyFundedDay: string | null;
  coveredThrough: string;
  days: CashDay[];
}
const DAY = 86400000;
function parseDay(day: string): number {
  const n = Date.parse(day + 'T00:00:00Z');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(n) || new Date(n).toISOString().slice(0, 10) !== day) throw new Error('Invalid calendar date');
  return n;
}
const dateOf = (n: number) => new Date(n).toISOString().slice(0, 10);
function cents(amount: number): number {
  if (!Number.isFinite(amount) || amount < 0 || !Number.isSafeInteger(Math.round(amount * 100))) throw new Error('Amounts must be finite and non-negative');
  return Math.round(amount * 100);
}
function occurrence(anchor: string, months: number): string {
  const date = new Date(parseDay(anchor));
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return dateOf(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(date.getUTCDate(), last)));
}
export function forecastRunway(input: RunwayInput): RunwayResult {
  const start = parseDay(input.asOf), end = parseDay(input.coveredThrough);
  if (end <= start || end - start > DAY * 366 * 5) throw new Error('Forecast coverage must be after the opening day and within five years');
  let available = cents(input.cashAed);
  const weekly = cents(input.weeklyEverydayAed), saving = cents(input.weeklySavingAed ?? 0);
  if (saving > weekly) throw new Error('Weekly saving exceeds everyday spending');
  const due = new Map<string, CashDay['bills']>();
  const ids = new Set<string>();
  for (const bill of input.bills) {
    if (!bill.id || ids.has(bill.id)) throw new Error('Bill IDs must be unique');
    ids.add(bill.id); cents(bill.amountAed); parseDay(bill.firstDue);
    if (bill.lastDue && parseDay(bill.lastDue) < parseDay(bill.firstDue)) throw new Error('Bill end precedes first due date');
    const repeat = bill.repeatMonths;
    if (repeat !== undefined && (!Number.isInteger(repeat) || repeat <= 0)) throw new Error('Invalid bill recurrence');
    for (let n = 0; ; n++) {
      const date = occurrence(bill.firstDue, n * (repeat ?? 0));
      if (date > input.coveredThrough || (bill.lastDue && date > bill.lastDue)) break;
      if (date > input.asOf) {
        due.set(date, [...(due.get(date) ?? []), { id: bill.id, name: bill.name, amountAed: cents(bill.amountAed) / 100 }]);
      }
      if (!repeat) break;
    }
  }
  const result: RunwayResult = { needsSavingsOn: null, lastFullyFundedDay: null, coveredThrough: input.coveredThrough, days: [] };
  if (available === 0) { result.needsSavingsOn = input.asOf; return result; }
  let previousEveryday = 0;
  for (let n = 1; start + n * DAY <= end; n++) {
    const date = dateOf(start + n * DAY);
    // Accumulate the fractional daily cost, so seven days always equal one week.
    const cumulative = Math.round((weekly - saving) * n / 7);
    const everyday = cumulative - previousEveryday;
    previousEveryday = cumulative;
    const bills = due.get(date) ?? [];
    available -= everyday + bills.reduce((sum, bill) => sum + cents(bill.amountAed), 0);
    result.days.push({ date, remainingAed: available / 100, everydayAed: everyday / 100, bills });
    if (available <= 0) {
      result.needsSavingsOn = date;
      result.lastFullyFundedDay = dateOf(start + (n - 1) * DAY);
      break;
    }
    result.lastFullyFundedDay = date;
  }
  return result;
}

/** Calendar display of the existing averaged spending scenario, not a dated-bill forecast.
 * Always anchor to the cash snapshot, never today's date, so reopening adds no runway.
 * Ceil identifies the first calendar day on which cash is exhausted.
 */
export function averageRunwayDate(asOf: string, cashAed: number, monthlySpendAed: number): string | null {
  const start = parseDay(asOf);
  if (!Number.isFinite(cashAed) || cashAed < 0 || !Number.isFinite(monthlySpendAed)) return null;
  if (cashAed === 0) return asOf;
  if (monthlySpendAed <= 0) return null;
  const days = Math.ceil(cashAed / monthlySpendAed * 365.25 / 12);
  const end = start + days * DAY;
  return Number.isFinite(new Date(end).getTime()) ? dateOf(end) : null;
}
